import React from 'react'
import { produce } from 'immer'
import { create, useStore } from 'zustand'
import { useRequiredContext } from '../../utils/use-required-context'
import { parseLogInput } from './parse-log-input'

type Store = ReturnType<typeof createRuleExecutionLogsStore>

type State = {
  trackingIsEnabled: boolean
  logs: RuleExecutionLog[]
  register: (item: RuleExecutionLog) => void
  enableTracking: (enable: boolean) => void
}

export type RuleExecutionLog = {
  id: number
  timestamp: string
  tag: string
  params: string
  success: boolean
  messages: MessageEntry[]
  parsed: Parsed[]
}

export type MessageEntry = {
  level: 'ERROR' | 'WARN' | 'INFO'
  message: string
  timestamp: string
  mdc?: Record<string, string>
  throwableMessage?: string
  stackTrace?: string
}

type Parsed = {
  id: number
  type: 'before-rule' | 'after-rule'
  tag: string
  uri: string
  timestamp: string
  params: string
}

const StoreContext = React.createContext<Store | null>(null)
const useStoreInstance = () => useRequiredContext(StoreContext)

/** This expects only one rule-execution-logs-store to be active */
export let storeRef: {current: Store | undefined} = {current: undefined}
const preStoreCreationBuffer: RuleExecutionLog[] = []


export function createRuleExecutionLogsStore() {
  const store = create<State>()((set, _get, _store) => ({
    trackingIsEnabled: false,
    logs: [],
    register: (item) => registerRuleExecutionLog(item, store),
    enableTracking: (enable) => set(produce((st: State) => { st.trackingIsEnabled = enable }))
  }))

  storeRef.current = store
  return store
}

/**
 * If no rule-execution-logs-store specified, then the last created store is used.
 * This is therefore only useful if we can assume only one rule-execution-logs-store is active.
 */
export function registerRuleExecutionLog(
  input: Omit<RuleExecutionLog, 'parsed'>,
  store?: Store,
): void {
  const logItem = parseLogInput(input)

  const target = store ?? storeRef.current
  if (!target) {
    if (preStoreCreationBuffer.length > 1337) throw new Error('a rule-execution-logs-store is supposed to exist by now')

    preStoreCreationBuffer.push(logItem)
    return
  }

  while (preStoreCreationBuffer.length) {
    const preStoreCreationItem = preStoreCreationBuffer.shift()
    if (!preStoreCreationItem) break

    target.setState(produce((state: State) => {
      state.logs.push(preStoreCreationItem)
    }))
  }

  target.setState(produce((state: State) => {
    state.logs.push(logItem)
  }))
}

export function ruleTrackingIsEnabled(store?: Store): boolean {
  const target = store ?? storeRef.current
  return target?.getState().trackingIsEnabled ?? false
}

export function useRuleExecutionLogs() {
  return useRuleExecutionLogsStore(st => st.logs)
}

export function useRegisterRuleExecutionLog() {
  return useRuleExecutionLogsStore(st => st.register)
}

export function useRuleTrackingIsEnabled() {
  return useRuleExecutionLogsStore(st => st.trackingIsEnabled)
}

export function useEnableRuleTracking() {
  return useRuleExecutionLogsStore(st => st.enableTracking)
}

export function RuleExecutionLogsStoreProvider(props: {
  children: React.ReactNode
}) {
  const ruleExecutionLogsStoreRef = React.useRef<Store>()
  if (ruleExecutionLogsStoreRef.current == null) {
    ruleExecutionLogsStoreRef.current = createRuleExecutionLogsStore()
  }

  return (
    <StoreContext.Provider value={ruleExecutionLogsStoreRef.current}>
      {props.children}
    </StoreContext.Provider>
  )
}

function useRuleExecutionLogsStore<T>(selector: (state: State) => T) {
  const store = useStoreInstance()
  return useStore(store, selector)
}
